import { Category, Module } from "@apis/index";
import AccessDenied from "@components/access-denied";
import {
  deletesvg,
  edit,
  eye,
  setting,
  switchOff,
  switchOn,
} from "@assets/icons";
import { FaStore } from "react-icons/fa";
import Column from "@components/column";
import DeleteModal from "@components/delete-modal";
import Svg from "@components/svg";
import Table from "@components/table";
import { useModal } from "@context/ModalContext";
import useConfig from "@hooks/useConfig";
import { RootState } from "@store/index";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryState } from "@hooks/useQueryState";
import { toast } from "react-toastify";
import {
  BannerContainer,
  BannerImage,
  BannerScrollWrapper,
  IconImage,
} from "./styles";
import StoreModal from "./StoreModal";

const CategoryTable = ({ moduleId }) => {
  const { bucketUrl } = useConfig();

  const {
    items: categories = [],
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.categories);

  const { showModal } = useModal();
  const [page, setPage] = useQueryState<number>("page", 1);
  const [search, setSearch] = useState("");

  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setError(null);
    try {
      await Category.list({
        state: "categories",
        params: { page, moduleId, parentId: 0, limit: 30, search },
      });
    } catch (err: any) {
      console.error("Failed to fetch categories:", err);
      setError(err.message || "Failed to load categories");
    }
  }, [moduleId, page, search]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setPage(1);
  }, [search]);

  const fallbackIcon = "https://via.placeholder.com/40";
  const fallbackBanner = "https://via.placeholder.com/80x40";

  const columns = [
    {
      title: "ID",
      key: "id",
      render: (item: any) => item.id,
    },
    {
      title: "Category Name",
      key: "name",
      render: (item: any) => item.name,
    },
    {
      title: "Marketing Title",
      key: "title",
      render: (item: any) => item?.marketingTitle,
    },
    // {
    //   title: "Store Type",
    //   key: "type",
    //   render: (item: any) => item?.type?.name || "N/A",
    // },
    // {
    //   title: "Zone",
    //   key: "zone",
    //   render: (item: any) => (
    //     <>
    //       {item?.zones?.length > 0 &&
    //         item.zones.map((z) => (
    //           <Column stylecolumn="justify-content:center">
    //             <p>{z.name}</p>
    //           </Column>
    //         ))}
    //     </>
    //   ),
    // },
    {
      title: "Icon",
      key: "icon",
      render: (item: any) => (
        <IconImage
          src={item.icon ? `${bucketUrl}/${item.icon}` : fallbackIcon}
          alt={item.name}
        />
      ),
    },
    {
      title: "Banner",
      key: "banner",
      render: (item: any) => (
        <BannerContainer>
          <BannerScrollWrapper>
            {item?.banners?.map((b) => (
              <BannerImage
                src={b.image ? `${bucketUrl}/${b.image}` : fallbackBanner}
                alt={item.name}
              />
            ))}
          </BannerScrollWrapper>
        </BannerContainer>
      ),
    },
    {
      title: "Order",
      key: "order",
      render: (item: any) => item?.order ?? "N/A",
    },
    {
      title: "Active",
      key: "isActive",
      render: (item: any) => (
        <Svg
          src={item.isActive ? switchOn : switchOff}
          onClick={async (status) => {
            const formData = new FormData();
            const payload = item.isActive ? "false" : "true";
            formData.append("isActive", payload);
            formData.append("parentId", "0");
            await Category.update({
              state: "categories",
              data: formData,
              id: item.id,
            })
              .then(() => {
                fetchCategories();
              })
              .catch((err) => {
                toast.error(err.message);
              });
          }}
        />
      ),
    },
    {
      title: "Show on Home Screen",
      key: "isShowOnHomeScreen",
      render: (item: any) => (
        <Svg
          src={item?.isShowOnHomeScreen ? switchOn : switchOff}
          onClick={async (status) => {
            const formData = new FormData();
            const payload = item.isShowOnHomeScreen ? "false" : "true";
            formData.append("isShowOnHomeScreen", payload);
            formData.append("parentId", "0");
            await Category.update({
              state: "categories",
              data: formData,
              id: item.id,
            })
              .then(() => {
                fetchCategories();
              })
              .catch((err) => {
                toast.error(err.message);
              });
          }}
        />
      ),
    },
    {
      title: "Show Banner on Home Screen",
      key: "banneronhome",
      render: (item: any) => (
        <Svg
          src={item?.isShowBannerOnHomeScreen ? switchOn : switchOff}
          onClick={async (status) => {
            const formData = new FormData();
            const payload = item.isShowBannerOnHomeScreen ? "false" : "true";
            formData.append("isShowBannerOnHomeScreen", payload);
            formData.append("parentId", "0");
            await Category.update({
              state: "categories",
              data: formData,
              id: item.id,
            })
              .then(() => {
                fetchCategories();
              })
              .catch((err) => {
                toast.error(err.message);
              });
          }}
        />
      ),
    },
  ];
  const navigate = useNavigate();

  if (error) {
    return (
      <AccessDenied
        message={error}
        onRetry={fetchCategories}
      />
    );
  }

  return (
    <Table
      data={categories}
      columns={columns}
      totalItems={totalItems}
      totalPages={totalPages}
      pageControlled={page}
      onPageChange={setPage}
      itemsPerPage={30}
      hasSearchbar={true}
      onSearchChange={setSearch}
      actions={[
        { key: "view", icon: eye, onClick: () => {} },
        {
          key: "edit",
          icon: edit,
          onClick: (item) => {
            navigate(
              `/admin/product-management/categories/${item.id}?module=${item.module.name}`,
              {
                state: item,
              },
            );
          },
        },

        {
          key: "remove",
          icon: deletesvg,
          onClick: async (item) => {
            showModal(
              <DeleteModal
                state={"categories"}
                api={Category}
                id={item.id}
                onSuccess={() => fetchCategories()}
              />,
            );
          },
        },
        {
          key: "stores",
          icon: <FaStore size={20} color="#4A4A4A" />,
          onClick: (item) => {
            showModal(
              <StoreModal categoryId={item.id} moduleId={item.module.id} />,
              { width: 900 },
            );
          },
        },
      ]}
    />
  );
};

export default CategoryTable;
